import {
	Box,
	Button,
	Card,
	Grid,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';

import { useNavigate, useParams } from 'react-router-dom';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import RichTextDisplay from '@/common/components/form-view/RichTextDisplay';
import {
	useChapterDetailQuery,
	useDeleteChapterMutation,
} from '@/api/information/informationApiSlice';
import AppImageView from '@/common/components/form-view/AppImageView';
import ChapterInfoForm from './components/ChapterInfoForm';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

export default function ChapterDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const { informationId, chapterId } = useParams();
	const [currentTab, setCurrentTab] = useState('zh');
	const showModal = useModal();
	const [deleteChapter] = useDeleteChapterMutation();
	const navigate = useNavigate();

	const { data: details } = useChapterDetailQuery(chapterId || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDelete = () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('information.deleteChapter')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('information.isDeleteChapter')}
					</Typography>
				}
				cancellationText=''
				confirmationText={t('button.delete')}
				confirmationButtonProps={{ color: 'error', variant: 'outlined' }}
				onConfirm={async () => {
					const response = await deleteChapter(chapterId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteChapterSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(
							`/information-manage/information-list/info/${informationId}`,
							{
								replace: true,
							}
						);
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.chapterDetail')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'} spacing={2}>
						<Button
							variant='contained'
							endIcon={<CreateOutlinedIcon />}
							onClick={() => {
								setType(FormType.edit);
							}}
						>
							{t('button.edit')}
						</Button>
						<Button
							variant='contained'
							endIcon={<DeleteOutlinedIcon />}
							onClick={() => {
								handleDelete();
							}}
						>
							{t('button.delete')}
						</Button>
					</Stack>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('information.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('information.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={t('information.chapterTitle')}
													value={
														currentTab == 'zh'
															? details?.data.titleZh
															: currentTab == 'en'
															? details?.data.titleEn
															: '-'
													}
												/>
											</Grid>
											{currentTab == 'zh' && (
												<Grid item xs={12}>
													<RichTextDisplay
														label={t('information.chapterText')}
														editorStateString={details?.data.contentZh || '-'}
													/>
												</Grid>
											)}
											{currentTab == 'en' && (
												<Grid item xs={12}>
													<RichTextDisplay
														label={t('information.chapterText')}
														editorStateString={details?.data.contentEn || '-'}
													/>
												</Grid>
											)}
											<Grid item xs={12}>
												<AppImageView
													label={t('emo.coverImage')}
													imageElement={
														details?.data.coverImage && (
															<img
																src={details?.data.coverImage.url}
																width={110}
																height={132}
																style={{
																	objectFit: 'contain',
																}}
															/>
														)
													}
												/>
											</Grid>
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<ChapterInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>
			</Box>
		</>
	);
}
